import { DraggingTableHeaders, FilterForm, OptionType, SignTitle } from '@/components'
import { Modal, Pagination, Tabs, Typography } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { t } from '@/i18n'
import {
  useGetBoundClientList,
  useGetBoundFishingEmailList,
  useGetBoundSecurityTestEmailList,
  useGetBoundSiteList,
} from './utils'

interface PropsType {
  open: boolean
  item: any
  onCancel: () => void
}

const SdDetail: React.FC<PropsType> = ({
  open,
  item: { id, phish_email_count, site_count, agent_count, email_count },
  onCancel,
}) => {
  const [searchParams, setSearchParams] = useState({ search: '', page: 1, limit: 10, areaId: id })
  const [filterParams, setFilterParams] = useState<{ [k: string]: string | undefined }>()
  const [activeKey, setActiveKey] = useState('1')
  const [total, setTotal] = useState(0)

  const [fishingEmailList, fishingEmailListLoading, getBoundFishingEmailList] =
    useGetBoundFishingEmailList()
  const [siteList, siteListLoading, getBoundSiteList] = useGetBoundSiteList()
  const [clientList, clientListLoading, getBoundClientList] = useGetBoundClientList()
  const [securityTestEmailList, securityTestEmailListLoading, getBoundSecurityTestEmailList] =
    useGetBoundSecurityTestEmailList()

  const handleChange = (key: string) => {
    setActiveKey(key)
    setSearchParams({ search: '', page: 1, limit: 10, areaId: id })
    setFilterParams(undefined)
  }

  const option = useMemo(() => {
    switch (activeKey) {
      case '1':
        return {
          search: {
            type: 'input',
            label: t('邮箱地址'),
          },
        }
      case '2':
        return {
          search: {
            type: 'input',
            label: t('web站点名称&地址'),
          },
        }
      case '3':
        return {
          search: {
            type: 'input',
            label: t('主机、IP、客户端别名'),
          },
        }
      case '4':
        return {
          search: {
            type: 'input',
            label: t('邮箱地址、关联主机地址'),
          },
        }
    }
  }, [activeKey]) as OptionType

  const columns = useMemo(() => {
    switch (activeKey) {
      case '1':
        return [
          {
            width: '3%',
          },
          {
            title: t('邮箱地址'),
            dataIndex: 'email',
            key: 'email',
            width: 260,
          },
          {
            title: t('姓名'),
            dataIndex: 'employeeName',
            key: 'employeeName',
            width: 120,
          },
          {
            title: t('部门名称'),
            dataIndex: 'groupName',
            key: 'groupName',
            width: 140,
            render: text => (
              <Typography.Text ellipsis={{ tooltip: text }} style={{ maxWidth: 140 }}>
                {text || '-'}
              </Typography.Text>
            ),
          },
          {
            title: t('公司名称'),
            dataIndex: 'companyName',
            key: 'companyName',
            width: 220,
          },
          {
            title: t('最近使用时间'),
            dataIndex: 'lastTaskTime',
            key: 'lastTaskTime',
            render: text => <div>{text || '-'}</div>,
          },
        ]
      case '2':
        return [
          {
            width: '3%',
          },
          {
            title: t('名称'),
            dataIndex: 'siteName',
            key: 'siteName',
            width: 330,
            render: text => (
              <Typography.Text ellipsis={{ tooltip: text }} style={{ maxWidth: 330 }}>
                {text || '-'}
              </Typography.Text>
            ),
          },
          {
            title: t('地址'),
            dataIndex: 'sitePath',
            key: 'sitePath',
            width: 190,
            render: text => (
              <Typography.Text ellipsis={{ tooltip: text }} style={{ maxWidth: 190 }}>
                {text || '-'}
              </Typography.Text>
            ),
          },
          {
            title: t('关联资产'),
            dataIndex: 'assetName',
            key: 'assetName',
            width: 220,
            render: text => <div>{text || '-'}</div>,
          },
          {
            title: t('最近使用时间'),
            dataIndex: 'lastTaskTime',
            key: 'lastTaskTime',
            render: text => <div>{text || '-'}</div>,
          },
        ]
      case '3':
        return [
          {
            width: '3%',
          },
          {
            title: t('主机名称'),
            dataIndex: 'name',
            key: 'name',
            width: 330,
          },
          {
            title: t('IP地址'),
            dataIndex: 'ips',
            key: 'ips',
            width: 190,
            render: text => (
              <Typography.Text ellipsis={{ tooltip: text.join(', ') }} style={{ maxWidth: 190 }}>
                {text.join(', ') || '-'}
              </Typography.Text>
            ),
          },
          {
            title: t('客户端别名'),
            dataIndex: 'alias',
            key: 'alias',
            width: 110,
          },
          {
            title: t('在线状态'),
            dataIndex: 'connStatus',
            key: 'connStatus',
            width: 115,
            render: (_, { connStatus }) => (
              <div>
                <span
                  style={{
                    background: connStatus === 'connected' ? '#67C23A' : '#999999',
                    display: 'inline-block',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    marginRight: '10px',
                  }}
                />
                <span>{connStatus === 'connected' ? t('在线') : t('离线')}</span>
              </div>
            ),
          },
          {
            title: t('最近使用时间'),
            dataIndex: 'lastTaskTime',
            key: 'lastTaskTime',
            render: text => <div>{text || '-'}</div>,
          },
        ]
      case '4':
        return [
          {
            width: '3%',
          },
          {
            title: t('关联主机名称'),
            dataIndex: 'hostName',
            key: 'hostName',
            width: 230,
          },
          {
            title: t('验证邮箱地址'),
            dataIndex: 'email',
            key: 'email',
            width: 290,
          },
          {
            title: t('协议类型'),
            dataIndex: 'agreement',
            key: 'agreement',
            width: 240,
            render: text => (
              <div>
                {text === 'email_pop3'
                  ? 'POP3'
                  : text === 'email_imap'
                  ? 'IMAP'
                  : text === 'email_unknown'
                  ? '-'
                  : 'OUTLOOK'}
              </div>
            ),
          },
          {
            title: t('最近使用时间'),
            dataIndex: 'lastTaskTime',
            key: 'lastTaskTime',
            render: text => <div>{text || '-'}</div>,
          },
        ]
    }
  }, [activeKey])

  useEffect(() => {
    if (id) {
      setSearchParams({ search: '', page: 1, limit: 10, areaId: id })
    }
  }, [id])

  useEffect(() => {
    if (open && searchParams.areaId) {
      switch (activeKey) {
        case '1':
          getBoundFishingEmailList({ ...searchParams, areaId: id })
            .then(res => {
              setTotal(res.data.total)
            })
            .catch(_ => {})

          break
        case '2':
          getBoundSiteList({ ...searchParams, areaId: id })
            .then(res => {
              setTotal(res.data.total)
            })
            .catch(_ => {})

          break
        case '3':
          getBoundClientList({ ...searchParams, limit: searchParams.limit })
            .then(res => {
              setTotal(res.data.total)
            })
            .catch(_ => {})
          break
        case '4':
          getBoundSecurityTestEmailList({ ...searchParams, areaId: id })
            .then(res => {
              setTotal(res.data.total)
            })
            .catch(_ => {})
          break
      }
    }
  }, [searchParams])

  return (
    <Wrapper
      title={<SignTitle show>{t('安全域详情')}</SignTitle>}
      width={1000}
      centered
      open={open}
      onCancel={() => {
        setSearchParams({ search: '', page: 1, limit: 10, areaId: '' })
        setActiveKey('1')
        setTotal(0)
        onCancel()
      }}
      footer={null}
    >
      <Tabs activeKey={activeKey} destroyInactiveTabPane type="card" onChange={handleChange}>
        <Tabs.TabPane tab={`${t('钓鱼邮箱')} (${phish_email_count || 0})`} key="1">
          <Box>
            <FilterForm
              option={option}
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              setSearchParams={setSearchParams}
              onReset={() => setSearchParams({ search: '', page: 1, limit: 10, areaId: id })}
            />
            <div style={{ padding: '0 20px', height: '396px', overflowY: 'scroll' }}>
              <DraggingTableHeaders
                columns={columns}
                dataSource={fishingEmailList}
                isScript
                pagination={false}
                loading={fishingEmailListLoading}
              />
            </div>
            <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={searchParams.page}
                pageSize={searchParams.limit}
                total={total}
                showTotal={totalCount => `${t('共')} ${totalCount} ${t('条')}`}
                showQuickJumper
                showSizeChanger
                onChange={(page, pageSize) => {
                  console.log('page', page, 'pageSize', pageSize)
                  setSearchParams({ ...searchParams, page, limit: pageSize })
                }}
              />
            </div>
          </Box>
        </Tabs.TabPane>
        <Tabs.TabPane tab={`${t('Web应用')} (${site_count})`} key="2">
          <Box>
            <FilterForm
              option={option}
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              setSearchParams={setSearchParams}
              onReset={() => setSearchParams({ search: '', page: 1, limit: 10, areaId: id })}
            />
            <div style={{ padding: '0 20px', height: '396px', overflowY: 'scroll' }}>
              <DraggingTableHeaders
                columns={columns}
                dataSource={siteList}
                isScript
                pagination={false}
                loading={siteListLoading}
              />
            </div>
            <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={searchParams.page}
                pageSize={searchParams.limit}
                total={total}
                showTotal={totalCount => `${t('共')} ${totalCount} ${t('条')}`}
                showQuickJumper
                showSizeChanger
                onChange={(page, pageSize) => {
                  setSearchParams({ ...searchParams, page, limit: pageSize })
                }}
              />
            </div>
          </Box>
        </Tabs.TabPane>
        <Tabs.TabPane tab={`${t('客户端')} (${agent_count})`} key="3">
          <Box>
            <FilterForm
              option={option}
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              setSearchParams={setSearchParams}
              onReset={() => setSearchParams({ search: '', page: 1, limit: 10, areaId: id })}
            />
            <div style={{ padding: '0 20px', height: '396px', overflowY: 'scroll' }}>
              <DraggingTableHeaders
                columns={columns}
                dataSource={clientList}
                isScript
                pagination={false}
                loading={clientListLoading}
              />
            </div>
            <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={searchParams.page}
                pageSize={searchParams.limit}
                total={total}
                showTotal={totalCount => `${t('共')} ${totalCount} ${t('条')}`}
                showQuickJumper
                showSizeChanger
                onChange={(page, pageSize) => {
                  console.log('page', page, 'pageSize', pageSize)
                  setSearchParams({ ...searchParams, page, limit: pageSize })
                }}
              />
            </div>
          </Box>
        </Tabs.TabPane>
        <Tabs.TabPane tab={`${t('验证邮箱')} (${email_count})`} key="4">
          <Box>
            <FilterForm
              option={option}
              filterParams={filterParams}
              setFilterParams={setFilterParams}
              setSearchParams={setSearchParams}
              onReset={() => setSearchParams({ search: '', page: 1, limit: 10, areaId: id })}
            />
            <div style={{ padding: '0 20px', height: '396px', overflowY: 'scroll' }}>
              <DraggingTableHeaders
                columns={columns}
                dataSource={securityTestEmailList}
                isScript
                pagination={false}
                loading={securityTestEmailListLoading}
              />
            </div>
            <div style={{ padding: '0 20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Pagination
                current={searchParams.page}
                pageSize={searchParams.limit}
                total={total}
                showTotal={totalCount => `${t('共')} ${totalCount} ${t('条')}`}
                showQuickJumper
                showSizeChanger
                onChange={(page, pageSize) => {
                  setSearchParams({ ...searchParams, page, limit: pageSize })
                }}
              />
            </div>
          </Box>
        </Tabs.TabPane>
      </Tabs>
    </Wrapper>
  )
}

export default SdDetail

const Wrapper = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    position: relative;

    .ant-modal-close {
      width: 36px;
      height: 36px;
      background: #d8d6f4;
      box-shadow: -1px 4px 10px 0px #c7c7c7;
      border-radius: 6px;
      border: none;
      color: #4238c9;
      font-size: 16px;
      font-weight: 500;
      position: absolute;
      right: -8px;
      top: -8px;
      transition: transform 0.3s ease;

      &:hover {
        transform: translate(-18px, 18px);
        box-shadow: 0 0 0 0;
      }

      .ant-modal-close-x {
        width: 100%;
        height: 100%;
        line-height: 36px;
        text-align: center;
      }
    }

    .ant-modal-header {
      height: 56px;
      background: #f2f0ff;
      border-bottom: none;
      margin: 0;
      padding: 19px 20px;
    }
  }

  .ant-modal-body {
    padding: 20px 0 24px 0;
    height: 600px;
  }

  .ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
    padding-left: 20px;
  }

  .ant-tabs-card > .ant-tabs-nav .ant-tabs-tab {
    width: 140px;
    border: none;
    background: #f1f3f8;
    margin-right: 4px;

    .ant-tabs-tab-btn {
      width: 100%;
      text-align: center;
    }
  }

  .ant-tabs-card.ant-tabs-top > .ant-tabs-nav .ant-tabs-tab {
    border-radius: 0;

    &:first-child {
      border-radius: 19px 0 0 19px;
    }

    &:nth-child(4) {
      border-radius: 0 19px 19px 0;
    }
  }

  .ant-tabs-tab-active {
    background: #e2e4f6 !important;
  }

  .filter-search .filter-form {
    width: 315px !important;

    .ant-form-item {
      width: 315px;
    }
  }
`

const Box = styled.div``
